class StorageService {
  constructor() {
    this.isAvailable = this.checkStorageAvailability();
    this.fallbackStore = new Map(); // In-memory fallback for SSR or storage unavailable
  }

  // Check if localStorage is available
  checkStorageAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('localStorage is not available, using in-memory storage');
      return false;
    }
  }

  // Set item with error handling and serialization
  setItem(key, value, options = {}) {
    try {
      const { expiry = null } = options;
      
      // Prepare value for storage
      let storageValue = {
        data: value,
        timestamp: Date.now(),
        expiry: expiry ? Date.now() + expiry : null
      };

      const serializedValue = JSON.stringify(storageValue);

      if (this.isAvailable) {
        localStorage.setItem(key, serializedValue);
      } else {
        this.fallbackStore.set(key, serializedValue);
      }

      // console.log(`📦 Storage SET: ${key}`, { 
      //   size: this.getStorageSize(serializedValue),
      //   hasExpiry: !!expiry 
      // });
      
      return true;
    } catch (error) {
      console.error(`❌ Storage SET error for key "${key}":`, error);
      
      // Try to handle quota exceeded error
      if (this.isQuotaError(error)) {
        // First drop anything already expired, then start evicting live API
        // cache entries (oldest first) until the write fits. Without this
        // second step a storage full of *fresh* entries can never accept a
        // new one, and every later setItem — including selectedExhibition
        // and auth data — silently fails.
        this.clearExpiredItems();

        const serializedValue = JSON.stringify({
          data: value,
          timestamp: Date.now(),
          expiry: options.expiry ? Date.now() + options.expiry : null
        });

        for (let attempt = 0; attempt < 20; attempt++) {
          try {
            if (this.isAvailable) {
              localStorage.setItem(key, serializedValue);
            } else {
              this.fallbackStore.set(key, serializedValue);
            }
            return true;
          } catch (retryError) {
            if (!this.isQuotaError(retryError)) throw retryError;
            if (!this.evictOldestCacheEntry(key)) break;
          }
        }

        console.error('❌ Storage still full after cleanup, key not stored:', key);
      }

      return false;
    }
  }

  // Get item with error handling and deserialization
  getItem(key, defaultValue = null) {
    try {
      let rawValue;
      
      if (this.isAvailable) {
        rawValue = localStorage.getItem(key);
      } else {
        rawValue = this.fallbackStore.get(key);
      }

      if (rawValue === null || rawValue === undefined) {
        return defaultValue;
      }

      // Try to parse stored value
      const parsedValue = JSON.parse(rawValue);
      
      // Check if it's our wrapped format
      if (parsedValue && typeof parsedValue === 'object' && 'data' in parsedValue && 'timestamp' in parsedValue) {
        // Check expiry
        if (parsedValue.expiry && Date.now() > parsedValue.expiry) {
          //console.log(`⏰ Storage item "${key}" expired, removing`);
          this.removeItem(key);
          return defaultValue;
        }
        
        // console.log(`📦 Storage GET: ${key}`, { 
        //   age: this.getAge(parsedValue.timestamp),
        //   expired: false 
        // });
        
        return parsedValue.data;
      }

      // Handle legacy data (direct values without our wrapper)
      // console.log(`📦 Storage GET (legacy): ${key}`);
      return parsedValue;

    } catch (error) {
      console.error(`❌ Storage GET error for key "${key}":`, error);
      
      // Try to get raw value as string fallback
      try {
        if (this.isAvailable) {
          const rawValue = localStorage.getItem(key);
          return rawValue || defaultValue;
        }
      } catch (fallbackError) {
        console.error('❌ Storage fallback also failed:', fallbackError);
      }
      
      return defaultValue;
    }
  }

  // Remove item with error handling
  removeItem(key) {
    try {
      if (this.isAvailable) {
        localStorage.removeItem(key);
      } else {
        this.fallbackStore.delete(key);
      }
      
      // console.log(`🗑️ Storage REMOVE: ${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Storage REMOVE error for key "${key}":`, error);
      return false;
    }
  }

  // Clear all storage
  clear() {
    try {
      if (this.isAvailable) {
        localStorage.clear();
      } else {
        this.fallbackStore.clear();
      }
      
      // console.log('🧹 Storage CLEARED');
      return true;
    } catch (error) {
      console.error('❌ Storage CLEAR error:', error);
      return false;
    }
  }

  // Get all keys
  getAllKeys() {
    try {
      if (this.isAvailable) {
        return Object.keys(localStorage);
      } else {
        return Array.from(this.fallbackStore.keys());
      }
    } catch (error) {
      console.error('❌ Storage GET_KEYS error:', error);
      return [];
    }
  }

  // Check if key exists
  hasItem(key) {
    try {
      if (this.isAvailable) {
        return localStorage.getItem(key) !== null;
      } else {
        return this.fallbackStore.has(key);
      }
    } catch (error) {
      console.error(`❌ Storage HAS_ITEM error for key "${key}":`, error);
      return false;
    }
  }

  // Get storage usage info
  getStorageInfo() {
    try {
      if (!this.isAvailable) {
        return {
          used: this.fallbackStore.size,
          total: 'unlimited (memory)',
          available: 'unlimited',
          percentage: 0
        };
      }

      let used = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          used += localStorage[key].length + key.length;
        }
      }

      const total = 5 * 1024 * 1024; // Approximate 5MB limit
      const available = total - used;
      const percentage = Math.round((used / total) * 100);

      return {
        used: this.formatBytes(used),
        total: this.formatBytes(total),
        available: this.formatBytes(available),
        percentage,
        items: Object.keys(localStorage).length
      };
    } catch (error) {
      console.error('❌ Storage INFO error:', error);
      return null;
    }
  }

  // Browsers disagree on how a full quota is reported
  isQuotaError(error) {
    return !!error && (
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      error.code === 22 ||
      error.code === 1014
    );
  }

  // Drop the least recently written `cache_` entry so a new write can fit.
  // Only API cache entries are evicted — user/auth/selection keys are never
  // sacrificed. Returns true if something was actually removed.
  evictOldestCacheEntry(protectedKey = null) {
    try {
      let oldestKey = null;
      let oldestTimestamp = Infinity;

      this.getAllKeys().forEach(key => {
        if (!key.startsWith('cache_') || key === protectedKey) return;

        let timestamp = 0; // unparseable entries are evicted first
        try {
          const raw = this.isAvailable ? localStorage.getItem(key) : this.fallbackStore.get(key);
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.timestamp === 'number') timestamp = parsed.timestamp;
        } catch {
          timestamp = 0;
        }

        if (timestamp < oldestTimestamp) {
          oldestTimestamp = timestamp;
          oldestKey = key;
        }
      });

      if (!oldestKey) return false;

      console.warn('🧹 Storage full — evicting cache entry:', oldestKey);
      this.removeItem(oldestKey);
      return true;
    } catch (error) {
      console.error('❌ Cache eviction error:', error);
      return false;
    }
  }

  // Clear expired items
  clearExpiredItems() {
    try {
      const keys = this.getAllKeys();
      let clearedCount = 0;

      keys.forEach(key => {
        try {
          const value = this.getItem(key);
          // getItem will automatically remove expired items
          if (value === null) {
            clearedCount++;
          }
        } catch (error) {
          // Remove corrupted items
          this.removeItem(key);
          clearedCount++;
        }
      });

      if (clearedCount > 0) {
        console.log(`🧹 Cleared ${clearedCount} expired/corrupted items`);
      }

      return clearedCount;
    } catch (error) {
      console.error('❌ Clear expired items error:', error);
      return 0;
    }
  }

  // Utility methods
  getStorageSize(value) {
    return this.formatBytes(new Blob([value]).size);
  }

  getAge(timestamp) {
    const ageMs = Date.now() - timestamp;
    const minutes = Math.floor(ageMs / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }

  formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // App-specific helper methods
  
  // Exhibition-related storage
  setExhibitionUserData(exhibitionId, userData) {
    return this.setItem(`userData_${exhibitionId}`, userData, { expiry: 7 * 24 * 60 * 60 * 1000 }); // 7 days
  }

  getExhibitionUserData(exhibitionId) {
    return this.getItem(`userData_${exhibitionId}`);
  }

  removeExhibitionUserData(exhibitionId) {
    return this.removeItem(`userData_${exhibitionId}`);
  }

  getAllLoggedInExhibitions() {
    const loggedInExhibitions = {};
    const keys = this.getAllKeys();
    
    keys.forEach(key => {
      if (key.startsWith('userData_')) {
        const exhibitionId = key.replace('userData_', '');
        const userData = this.getItem(key);
        
        if (userData && userData.token) {
          loggedInExhibitions[exhibitionId] = true;
        }
      }
    });
    
    return loggedInExhibitions;
  }

  // Visitor-related storage
  setVisitorData(data) {
    return this.setItem('visitorData', data, { expiry: 30 * 24 * 60 * 60 * 1000 }); // 30 days
  }

  getVisitorData() {
    return this.getItem('visitorData');
  }

  removeVisitorData() {
    return this.removeItem('visitorData');
  }

  // Exhibition selection storage
  setSelectedExhibition(exhibition) {
    return this.setItem('selectedExhibition', exhibition, { expiry: 24 * 60 * 60 * 1000 }); // 1 day
  }

  getSelectedExhibition() {
    return this.getItem('selectedExhibition');
  }

  removeSelectedExhibition() {
    return this.removeItem('selectedExhibition');
  }

  // Profile ID storage
  setProfileID(profileId) {
    return this.setItem('ProfileID', profileId, { expiry: 30 * 24 * 60 * 60 * 1000 }); // 30 days
  }

  getProfileID() {
    return this.getItem('ProfileID');
  }

  removeProfileID() {
    return this.removeItem('ProfileID');
  }

  hasValidProfileID() {
    const profileId = this.getProfileID();
    return !!profileId && profileId !== 'undefined' && profileId !== 'null';
  }

  // Language preference storage
  setLanguagePreference(language) {
    return this.setItem('languagePreference', language);
  }

  getLanguagePreference() {
    return this.getItem('languagePreference', 'en'); // Default to English
  }

  // Exhibition settings storage
  setExhibitionSettings(exhibitionId, settings) {
    return this.setItem(`exhibitionSettings_${exhibitionId}`, settings, { expiry: 24 * 60 * 60 * 1000 });
  }

  getExhibitionSettings(exhibitionId) {
    return this.getItem(`exhibitionSettings_${exhibitionId}`);
  }

  // Cache management for API responses
  setCacheData(key, data, expiryMinutes = 5) {
    const expiry = expiryMinutes * 60 * 1000; // Convert to milliseconds
    return this.setItem(`cache_${key}`, data, { expiry });
  }

  getCacheData(key) {
    return this.getItem(`cache_${key}`);
  }

  /**
   * Read a cache entry without deleting it when it has expired, reporting
   * whether it is still fresh.
   *
   * getCacheData/getItem evict on read, which would throw away the copy the
   * network-failure fallback wants. Callers that need a stale fallback read
   * through here instead: `{ data, expired }`, or null when nothing is stored.
   */
  getCacheEntry(key) {
    try {
      const storageKey = `cache_${key}`;
      const raw = this.isAvailable
        ? localStorage.getItem(storageKey)
        : this.fallbackStore.get(storageKey);

      if (raw === null || raw === undefined) return null;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'data' in parsed && 'timestamp' in parsed) {
        return {
          data: parsed.data,
          expired: !!parsed.expiry && Date.now() > parsed.expiry
        };
      }

      // Legacy entry written without our wrapper — treat as fresh
      return { data: parsed, expired: false };
    } catch (error) {
      console.error(`❌ Cache entry GET error for key "${key}":`, error);
      return null;
    }
  }

  // Read a cache entry even if it has expired, without deleting it.
  getStaleCacheData(key) {
    const entry = this.getCacheEntry(key);
    return entry ? entry.data : null;
  }

  clearAllCaches() {
    const keys = this.getAllKeys();
    let clearedCount = 0;
    
    keys.forEach(key => {
      if (key.startsWith('cache_')) {
        this.removeItem(key);
        clearedCount++;
      }
    });
    
    //console.log(`🧹 Cleared ${clearedCount} cache items`);
    return clearedCount;
  }

  // Debug methods
  // debugStorage() {
  //   console.group('🔍 Storage Debug Info');
  //   console.log('Storage available:', this.isAvailable);
  //   console.log('Storage info:', this.getStorageInfo());
  //   console.log('All keys:', this.getAllKeys());
  //   console.log('Logged in exhibitions:', this.getAllLoggedInExhibitions());
  //   console.log('Visitor data exists:', !!this.getVisitorData());
  //   console.log('Selected exhibition:', this.getSelectedExhibition());
  //   console.log('Profile ID:', this.getProfileID());
  //   console.groupEnd();
  // }

  // Cleanup method - call this periodically or on app start
  cleanup() {
    const expiredCount = this.clearExpiredItems();
    const cacheCount = this.clearAllCaches();
    
    //console.log(`🧹 Storage cleanup complete: ${expiredCount + cacheCount} items removed`);
    
    return {
      expiredItems: expiredCount,
      cacheItems: cacheCount,
      total: expiredCount + cacheCount
    };
  }
}

// Create and export singleton instance
export const storageService = new StorageService();

// Export the class for testing or multiple instances if needed
export { StorageService };

// Export storage keys constants to avoid typos
export const STORAGE_KEYS = {
  USER_DATA_PREFIX: 'userData_',
  VISITOR_DATA: 'visitorData',
  SELECTED_EXHIBITION: 'selectedExhibition',
  PROFILE_ID: 'ProfileID',
  LANGUAGE_PREFERENCE: 'languagePreference',
  EXHIBITION_SETTINGS_PREFIX: 'exhibitionSettings_',
  CACHE_PREFIX: 'cache_'
};

export default storageService;